import React, {useCallback, useEffect, useMemo, useState} from "react";
import { DHSurveyData } from "../../../../types/DHSurveyData";
import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import { numberValidator } from "../../../../utils/Validator";
import {Button, Input, message, Select, Space, Table} from "antd";
import {ColumnsType} from "antd/es/table";
import {
    addDHSurveyRow,
    deleteDHSurveyByDhsid,
    fetchDHSurveyByPid,
    updateDHSurveyRow
} from "../../../../backendInteraction/DHSurveyApiFetch";

interface DHSurveyTableProps {
    data: DHSurveyData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const DHSurveyTable: React.FC<DHSurveyTableProps> = ({data, plodId, contractNo, isView, isAddNewRow, holeId, setHoleId, setIsAddNewRow}) => {
    const [dhSurveyData, setDhSurveyData] = useState<DHSurveyData []>([]);

    const [toolTypeOptions, setToolTypeOptions] = useState<{value: string; label: string}[]>([]);
    const [aziTypeOptions, setAziTypeOptionsOptions] = useState<{value: string; label: string}[]>([]);

    const [editingDhsId, setEditingDhsID] = useState<number | null>(null);
    const [editData, setEditData] = useState<DHSurveyData | null>(null);


    const loadOptions = useCallback(async () => {
        if (contractNo) {
            const toolTypes = await fetchSelectOptionsByContractNO(contractNo, "DHSurveyToolType");
            const aziTypes = await fetchSelectOptionsByContractNO(contractNo, "AziType");
            setToolTypeOptions(toolTypes.map(toolType => ({ value: toolType, label: toolType })));
            setAziTypeOptionsOptions(aziTypes.map(aziType => ({ value: aziType, label: aziType })));
        } else {
            setToolTypeOptions([]);
            setAziTypeOptionsOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        setDhSurveyData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingDhsId !== -1) {
            const len = dhSurveyData.length;
            const newRow: DHSurveyData = {
                key: -1,
                dhs_id: -1,
                hole_id: len > 0 ? dhSurveyData[len - 1].hole_id || holeId : holeId,
                depth: "",
                tool_type: "",
                tool_no: "",
                dip: "",
                azimuth: "",
                azi_type: "",
                mag_int: "",
                mag_dip: "",
                grav_roll: "",
                temp: "",
                pid: plodId ?? -1,
            };
            setDhSurveyData(prevData => {
                const hasNewRow = prevData.some(item => item.dhs_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingDhsID(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingDhsId]);

    useEffect(() => {
        if (isView) {
            if (editingDhsId === -1) {
                setDhSurveyData(prevData => prevData.filter(item => item.dhs_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingDhsID(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { depth, tool_type, dip, azimuth, mag_int, mag_dip, grav_roll, temp, hole_id } = editData;

        if(!!depth) {
            const isNumber = numberValidator(depth, 'Depth');
            if (!isNumber) return;
        }

        if(!tool_type) {
            message.error('The "ToolType" value can not be empty.');
            return;
        }

        if(!!dip) {
            const isNumber = numberValidator(dip, 'Dip');
            if (!isNumber) return;
        }

        if(!!azimuth) {
            const isNumber = numberValidator(azimuth, 'Azimuth');
            if (!isNumber) return;

            if (parseFloat(azimuth) < 0 || parseFloat(azimuth) > 360) {
                message.error('The "Azimuth" value must be between 0 and 360.');
                return;
            }
        }

        if(!!mag_int) {
            const isNumber = numberValidator(mag_int, 'Mag Int');
            if (!isNumber) return;
        }

        if(!!mag_dip) {
            const isNumber = numberValidator(mag_dip, 'Mag Dip');
            if (!isNumber) return;
        }

        if(!!grav_roll) {
            const isNumber = numberValidator(grav_roll, 'Grav Roll');
            if (!isNumber) return;
        }

        if(!!temp) {
            const isNumber = numberValidator(temp, 'Temp');
            if (!isNumber) return;
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingDhsId === -1) {
            const success = await addDHSurveyRow(editData, aid);
            if (success) {
                setDhSurveyData(await fetchDHSurveyByPid(plodId ?? -1, aid));
                message.success("New dh-survey added successfully!");
            } else {
                message.error("Failed to add new dh-survey.");
            }
        } else {
            const success = await updateDHSurveyRow(editingDhsId ?? -1, editData);
            if (success) {
                setDhSurveyData(await fetchDHSurveyByPid(plodId ?? -1, aid));
                message.success("DH Survey updated successfully!");
            } else {
                message.error("Failed to update dh-survey.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingDhsId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingDhsID(null);
        setEditData(null);
    }, [editData, editingDhsId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: DHSurveyData) => {
        console.log("Cancel editing");
        setEditingDhsID(null);
        setEditData(null);

        if (record.dhs_id === -1) {
            setDhSurveyData(prevData => prevData.filter(item => item.dhs_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: DHSurveyData) => {
        console.log("Editing row:", record);

        if (editingDhsId === -1) {
            setDhSurveyData(prevData => prevData.filter(item => item.dhs_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingDhsID(prevId => {
            if (prevId !== null) cancelEditing({ dhs_id: prevId } as DHSurveyData);
            return record.dhs_id;
        });

        setEditData({ ...record });
    }, [editingDhsId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: DHSurveyData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteDHSurveyByDhsid(record.dhs_id);
        if (success) {
            setDhSurveyData(await fetchDHSurveyByPid(plodId ?? -1, aid));
            message.success("DH Survey deleted successfully!");
        } else {
            message.error("Failed to delete dh-survey.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof DHSurveyData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<DHSurveyData> = useMemo(() => [
        {
            title: 'Hole ID',
            dataIndex: 'hole_id',
            key: 'hole_id',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: 'Depth',
            dataIndex: 'depth',
            key: 'depth',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.depth}
                        onChange={(e) => handleInputChange("depth", e.target.value)}
                    />
                ) : (
                    record.depth
                ),
        },
        {
            title: 'ToolType',
            dataIndex: 'tool_type',
            key: 'tool_type',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={toolTypeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.tool_type || ""}
                        onChange={(value) => handleInputChange("tool_type", value || "")}
                    />
                ) : (
                    record.tool_type
                ),
        },
        {
            title: 'ToolNo',
            dataIndex: 'tool_no',
            key: 'tool_no',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.tool_no}
                        onChange={(e) => handleInputChange("tool_no", e.target.value)}
                    />
                ) : (
                    record.tool_no
                ),
        },
        {
            title: 'Dip',
            dataIndex: 'dip',
            key: 'dip',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.dip}
                        onChange={(e) => handleInputChange("dip", e.target.value)}
                    />
                ) : (
                    record.dip
                ),
        },
        {
            title: 'Azimuth',
            dataIndex: 'azimuth',
            key: 'azimuth',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.azimuth}
                        onChange={(e) => handleInputChange("azimuth", e.target.value)}
                    />
                ) : (
                    record.azimuth
                ),
        },
        {
            title: 'AziType',
            dataIndex: 'azi_type',
            key: 'azi_type',
            width: "15%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={aziTypeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.azi_type || ""}
                        onChange={(value) => handleInputChange("azi_type", value || "")}
                    />
                ) : (
                    record.azi_type
                ),
        },
        {
            title: 'MagInt',
            dataIndex: 'mag_int',
            key: 'mag_int',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.mag_int}
                        onChange={(e) => handleInputChange("mag_int", e.target.value)}
                    />
                ) : (
                    record.mag_int
                ),
        },
        {
            title: 'MagDip',
            dataIndex: 'mag_dip',
            key: 'mag_dip',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.mag_dip}
                        onChange={(e) => handleInputChange("mag_dip", e.target.value)}
                    />
                ) : (
                    record.mag_dip
                ),
        },
        {
            title: 'GravRoll',
            dataIndex: 'grav_roll',
            key: 'non_chargeable_metres',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.grav_roll}
                        onChange={(e) => handleInputChange("grav_roll", e.target.value)}
                    />
                ) : (
                    record.grav_roll
                ),
        },
        {
            title: 'Temp',
            dataIndex: 'temp',
            key: 'temp',
            width: "7%",
            render: (_, record) =>
                editingDhsId === record.dhs_id ? (
                    <Input
                        value={editData?.temp}
                        onChange={(e) => handleInputChange("temp", e.target.value)}
                    />
                ) : (
                    record.temp
                ),
        },
        ...(!isView
            ? [{
                title: 'Action',
                key: 'action',
                width: "15%",
                render: (_, record) => (
                    <Space size="small">
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingDhsId === record.dhs_id ? saveChanges() : startEditing(record))}>
                            {editingDhsId === record.dhs_id ? "Save" : "Edit"}
                        </Button>
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingDhsId === record.dhs_id ? cancelEditing(record) : deleteRow(record))}>
                            {editingDhsId === record.dhs_id ? "Cancel" : "Delete"}
                        </Button>
                    </Space>
                ),
            },
            ] as ColumnsType<DHSurveyData>
            : []),
    ], [isView, editingDhsId, toolTypeOptions, aziTypeOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<DHSurveyData>
            columns={columns}
            pagination={false}
            dataSource={dhSurveyData}
        />
    );
}

export default  DHSurveyTable;